import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import TinyMceEditor from "../../../../components/tinymce-editor";
import {
  ACCENT_COLOR,
  FIELD_RADIUS,
  SECTION_BG,
  SECTION_RADIUS,
  inputSx,
} from "../../styles";
import { SectionHeader } from "../shared/Fields";

type AdditionalDetailsSectionProps = {
  wishList: string[];
  onRemoveWishItem: (item: string) => void;
  newWishItem: string;
  onNewWishItemChange: (value: string) => void;
  onAddWishItem: () => void;
  notes: string;
  onNotesChange: (value: string) => void;
  designerNotes: string;
  onDesignerNotesChange: (value: string) => void;
};

const AdditionalDetailsSection = ({
  wishList,
  onRemoveWishItem,
  newWishItem,
  onNewWishItemChange,
  onAddWishItem,
  notes,
  onNotesChange,
  designerNotes,
  onDesignerNotesChange,
}: AdditionalDetailsSectionProps) => (
  <Box
    sx={{
      backgroundColor: SECTION_BG,
      borderRadius: SECTION_RADIUS,
      p: { xs: 2, md: 3 },
    }}
  >
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{
          color: "#000",
          fontFamily: "var(--Font-Family-Titles, Jost)",
          fontSize: "var(--Font-Size-2XL, 25px)",
          fontStyle: "normal",
          fontWeight: 600,
          lineHeight: "var(--Font-Line-Height-LG, 40px)",
        }}
      >
        Additional details and key considerations.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Add your goals and any additional notes the designer may want to know.
      </Typography>
    </Box>
    <Stack spacing={3} mt={2}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ fontSize: "16px" }}>
          Client Wish List (Goals)
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2} sx={{ fontSize: "13px" }}>
          List specific features or requirements the client wants (e.g., marble countertops,
          LED lighting).
        </Typography>
        <Stack spacing={2}>
          {wishList.map((item) => (
            <Box
              key={item}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                border: "1px solid #E3E7EF",
                borderRadius: FIELD_RADIUS,
                backgroundColor: "#fff",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckIcon fontSize="small" />
                <Typography variant="body2">{item}</Typography>
              </Stack>
              <IconButton size="small" onClick={() => onRemoveWishItem(item)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <TextField
            value={newWishItem}
            onChange={(event) => onNewWishItemChange(event.target.value)}
            placeholder="Enter wish list item"
            size="small"
            fullWidth
            onKeyPress={(event) => {
              if (event.key === "Enter") {
                onAddWishItem();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CheckIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => onNewWishItemChange("")}
                    disabled={!newWishItem}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />
          <Button
            onClick={onAddWishItem}
            variant="outlined"
            disabled={!newWishItem.trim()}
            sx={{
              borderColor: "#E25A8A",
              borderStyle: "dashed",
              borderWidth: 1,
              borderRadius: FIELD_RADIUS,
              color: "#C2185B",
              width: "100%",
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#C2185B",
                backgroundColor: "rgba(194, 24, 91, 0.04)",
              },
            }}
          >
            + Add Wish List Item
          </Button>
        </Stack>
      </Box>

      <Box>
        <TinyMceEditor
          label="What are your Goals for this project?"
          value={notes}
          onChange={(html) => onNotesChange(html)}
          height={220}
          toolbar="bold italic underline | alignleft aligncenter alignright | bullist numlist | link"
          plugins="lists link"
        />
      </Box>
      <Box>
        <TinyMceEditor
          label="Note to Designer: Anything else you'd like your designer to know?"
          value={designerNotes}
          onChange={(html) => onDesignerNotesChange(html)}
          height={220}
          toolbar="bold italic underline | alignleft aligncenter alignright | bullist numlist | link"
          plugins="lists link"
        />
      </Box>
    </Stack>
  </Box>
);

export default AdditionalDetailsSection;
